import { UseFormReturn } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import LanguageSwitcher from '@/components/shared/languageSelect'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { ImageUploadV2 } from '@/components/shared/inputComponents/ImageUploadV2'
import { VideoUploadV2 } from '@/components/shared/inputComponents/VideoUploadV2'
import CtaEditor from '@/components/shared/CtaEditor'
import { PageParentSelect } from './PageParentSelect'
import PageTagSelector from './PageTagSelector'
import { PageFormValues } from '../types'
import { generateSlug, sanitizeSlug } from '../slugUtils'

const SETTINGS_FORM_ID = 'page-settings-form'

interface PageSettingsPanelProps {
  form: UseFormReturn<PageFormValues>
  languageId: number
  pageId: string
  heroImageUrl?: string
  onSave: (values: PageFormValues) => void
  // Omitted entirely on a create flow (this panel is only ever mounted for
  // an existing page today, but the guard keeps that assumption from
  // silently breaking if this panel is ever reused for create).
  onDeleteClick?: () => void
  deleteInfoLoading?: boolean
}

export default function PageSettingsPanel({
  form,
  languageId,
  pageId,
  heroImageUrl,
  onSave,
  onDeleteClick,
  deleteInfoLoading,
}: PageSettingsPanelProps) {
  // Auto-generate slug from the title while it is still empty / untouched.
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    const currentSlug = form.getValues(`page_profiles.${languageId}.slug`)
    const currentTitle = form.getValues(`page_profiles.${languageId}.title`)

    form.setValue(`page_profiles.${languageId}.title`, newTitle, {
      shouldDirty: true,
    })

    if (!currentSlug || currentSlug === generateSlug(currentTitle || '')) {
      form.setValue(
        `page_profiles.${languageId}.slug`,
        generateSlug(newTitle),
        { shouldDirty: true }
      )
    }
  }

  return (
    <Form {...form}>
      <form
        id={SETTINGS_FORM_ID}
        onSubmit={form.handleSubmit(onSave)}
        className="space-y-6"
      >
        {/* Published + Language */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              checked={form.watch('published')}
              onCheckedChange={val =>
                form.setValue('published', val, { shouldDirty: true })
              }
            />
            <span className="text-sm text-muted-foreground">
              {form.watch('published') ? 'Published' : 'Draft'}
            </span>
          </div>
          <LanguageSwitcher />
        </div>

        {/* Chromeless: render this page without navbar/footer (e.g. full-viewport flipbook pages) */}
        <div className="flex items-center gap-2">
          <Switch
            checked={form.watch('chromeless')}
            onCheckedChange={val =>
              form.setValue('chromeless', val, { shouldDirty: true })
            }
          />
          <span className="text-sm text-muted-foreground">
            Chromeless (hide navbar &amp; footer)
          </span>
        </div>

        {/* Private: gate this page behind portal login */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Switch
              checked={form.watch('private')}
              onCheckedChange={val =>
                form.setValue('private', val, { shouldDirty: true })
              }
            />
            <span className="text-sm text-muted-foreground">Private</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Only signed-in portal users can view this page and its subpages;
            visitors are redirected to login.
          </p>
        </div>

        {/* Page Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Page Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InputFieldContainer
              label="Code"
              description="Unique identifier (lowercase, hyphens only)"
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      placeholder="e.g., about-us"
                      type="text"
                      {...field}
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Arrangement"
              description="Order in navigation (lower = first)"
              control={form.control}
              name="arrangement"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={e =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Parent Page"
              description="Set a parent to nest this page under another"
              control={form.control}
              name="parentId"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <PageParentSelect
                      value={field.value ?? null}
                      onChange={val => field.onChange(val)}
                      excludeId={pageId}
                      placeholder="No parent (root page)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Hero Image"
              description="Main hero image displayed at the top of the page"
              control={form.control}
              name="hero_imageId"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <ImageUploadV2
                      field={field}
                      imageName="page-hero"
                      existingImageUrl={heroImageUrl}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Hero Video"
              description="Optional. Uploads a self-hosted MP4 that autoplays muted/looped over the hero. Keep it short and web-optimized; the Hero Image is used as the instant poster/fallback."
              control={form.control}
              name="hero_video_link"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <VideoUploadV2 field={field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Hero Display"
              description="How the hero image is displayed at the top of the page"
              control={form.control}
              name="hero_display"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Select
                      value={field.value ?? 'full_width'}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select hero display" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_width">Full width</SelectItem>
                        <SelectItem value="content_width">
                          Content width
                        </SelectItem>
                        <SelectItem value="none">No hero</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Hero Height"
              description="Height of the hero section — leave blank to use the default"
              control={form.control}
              name="hero_height_value"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="Default"
                        className="w-24"
                        {...field}
                        value={field.value ?? ''}
                        onChange={e =>
                          field.onChange(
                            e.target.value === ''
                              ? null
                              : parseInt(e.target.value)
                          )
                        }
                      />
                      <Select
                        value={form.watch('hero_height_unit') ?? 'vh'}
                        onValueChange={val =>
                          form.setValue(
                            'hero_height_unit',
                            val as 'px' | 'vh',
                            { shouldDirty: true }
                          )
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Unit" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="px">px</SelectItem>
                          <SelectItem value="vh">% of screen (vh)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Tags"
              description="Assign tags to this page"
              control={form.control}
              name="tag_ids"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <PageTagSelector
                      value={field.value ?? []}
                      onChange={ids => field.onChange(ids)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Content (Language-specific) */}
        <Card>
          <CardHeader>
            <CardTitle>Content (Language-specific)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InputFieldContainer
              label="Title"
              description="Page title for display and SEO"
              control={form.control}
              name={`page_profiles.${languageId}.title`}
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      placeholder="e.g., About Us"
                      type="text"
                      {...field}
                      value={field.value ?? ''}
                      onChange={handleTitleChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Subtitle"
              description="Optional subtitle displayed below the title"
              control={form.control}
              name={`page_profiles.${languageId}.subtitle`}
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      placeholder="e.g., Discover our story"
                      type="text"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Slug"
              description="URL path (auto-generated from title)"
              control={form.control}
              name={`page_profiles.${languageId}.slug`}
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      placeholder="e.g., about-us"
                      type="text"
                      {...field}
                      value={field.value ?? ''}
                      onChange={e =>
                        field.onChange(sanitizeSlug(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Meta Description"
              description="SEO description (max 160 characters)"
              control={form.control}
              name={`page_profiles.${languageId}.meta_description`}
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Textarea
                      placeholder="Brief description for search engines..."
                      className="resize-none"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Call To Action (Language-specific label/URL, shared page target) */}
        <Card>
          <CardHeader>
            <CardTitle>Call To Action</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <CtaEditor
              control={form.control}
              languageId={languageId}
              labelName={`page_profiles.${languageId}.cta_label`}
              linkName={`page_profiles.${languageId}.cta_link`}
              newTabName="cta_new_tab"
              linkPageIdName="cta_link_page_id"
              linkTypeName="cta_link_type"
              linkCategoryIdName="cta_link_category_id"
              pageIdToExclude={pageId}
            />
          </CardContent>
        </Card>

        {/* Array-root validation error (e.g. "At least one language must have
            title and slug filled"). The schema's refine attaches at the
            page_profiles array root, which has no per-field FormMessage, so we
            surface it here near the Save area. */}
        {(() => {
          const profilesError = form.formState.errors.page_profiles
          const message = profilesError?.root?.message ?? profilesError?.message
          return message ? (
            <p className="text-sm font-medium text-destructive">{message}</p>
          ) : null
        })()}

        {onDeleteClick && (
          <div className="border-t pt-4 mt-4">
            <Button
              type="button"
              variant="destructive"
              onClick={onDeleteClick}
              disabled={deleteInfoLoading}
            >
              Delete page
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}

export { SETTINGS_FORM_ID }
